import { useState } from 'react';
import { X, Package, AlertTriangle } from 'lucide-react';
import toast from 'react-hot-toast';
import { useMoveTestCases } from '../../hooks/useTestCases';
import { useSpecs } from '../../hooks/useSpecifications';
import Spinner from '../ui/Spinner';
import type { TestCaseListItem } from '../../services/testCaseService';

interface Props {
  projectId: string;
  selectedTCs: TestCaseListItem[];
  currentSpecId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export default function TCMoveModal({
  projectId,
  selectedTCs,
  currentSpecId,
  onClose,
  onSuccess,
}: Props) {
  const [targetSpecId, setTargetSpecId] = useState('');
  const moveMutation = useMoveTestCases(projectId);
  const { data: specs } = useSpecs(projectId);

  // 현재 명세 정보
  const currentSpec = specs?.find((s) => s.id === currentSpecId);

  // 이동 대상 후보: 현재 명세 제외
  const targetSpecs = (specs ?? []).filter((s) => s.id !== currentSpecId);

  const handleMove = async () => {
    if (!targetSpecId) {
      toast.error('이동할 명세를 선택해주세요.');
      return;
    }

    try {
      const result = await moveMutation.mutateAsync({
        testCaseIds: selectedTCs.map((tc) => tc.id),
        targetSpecId,
      });

      const targetSpec = targetSpecs.find((s) => s.id === targetSpecId);
      toast.success(
        `${result.movedCount}개 TC가 ${targetSpec?.functionCode ?? '선택한 명세'}로 이동되었습니다.`,
      );

      // 경고가 있으면 추가 토스트 표시
      if (result.warnings.length > 0) {
        toast(
          `${result.warnings.length}개 TC에 완료된 테스트 결과가 있습니다. 완료된 결과는 원래 명세의 테스트 기록에 그대로 보존됩니다.`,
          { icon: '⚠️', duration: 5000, style: { background: '#FEF3C7', color: '#92400E' } },
        );
      }

      onSuccess();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || 'TC 이동에 실패했습니다.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Package className="size-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">TC 명세 이동</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 rounded transition-colors"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* 선택된 TC 목록 */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              선택된 TC: {selectedTCs.length}개
            </label>
            <div className="mt-2 max-h-[200px] overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {selectedTCs.map((tc) => (
                <div key={tc.id} className="px-3 py-2 text-sm">
                  <span className="font-mono text-sm text-gray-500 mr-2">{tc.tcId}</span>
                  <span className="text-gray-900">{tc.title}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 현재 명세 */}
          <div>
            <label className="text-sm font-medium text-gray-700">현재 명세</label>
            <div className="mt-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm text-gray-600">
              {currentSpec
                ? `${currentSpec.functionCode} - ${currentSpec.title}`
                : currentSpecId}
            </div>
          </div>

          {/* 이동할 명세 선택 */}
          <div>
            <label className="text-sm font-medium text-gray-700">
              이동할 명세 <span className="text-red-500">*</span>
            </label>
            <select
              value={targetSpecId}
              onChange={(e) => setTargetSpecId(e.target.value)}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
            >
              <option value="">명세 선택</option>
              {targetSpecs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.functionCode} - {s.title}
                </option>
              ))}
            </select>
          </div>

          {/* 안내 메시지 */}
          <div className="flex items-start gap-2 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertTriangle className="size-4 text-amber-500 mt-0.5 shrink-0" />
            <p className="text-sm text-amber-700">
              이동된 TC의 기존 테스트 결과는 원래 명세의 테스트 기록에 그대로 보존됩니다.
              이동 대상 명세의 기존 테스트 실행에는 TC가 추가되지 않으며,
              다음 회차 생성 시 포함됩니다.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            취소
          </button>
          <button
            onClick={handleMove}
            disabled={!targetSpecId || moveMutation.isPending}
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {moveMutation.isPending ? (
              <Spinner className="size-4" />
            ) : (
              <Package className="size-4" />
            )}
            이동
          </button>
        </div>
      </div>
    </div>
  );
}
